import React from "react";
import { C } from "../../core/constants";
import { hexToRgba } from "../../core/color";
import { useI18n } from "../../features/i18n/LocaleProvider";

function CreditOrb({ acc, size = 28 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        background: `radial-gradient(circle at 35% 35%, ${acc.secondary}, ${acc.primary}88)`,
        boxShadow: `0 0 14px ${hexToRgba(acc.primary, 0.45)}`,
      }}
    />
  );
}

function PremiumBadge({ acc, label, compact = false, iconOnly = false }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: iconOnly ? 0 : 4,
        borderRadius: 999,
        border: `1px solid ${hexToRgba(acc.secondary, compact ? 0.4 : 0.45)}`,
        background: hexToRgba(acc.secondary, compact ? 0.1 : 0.14),
        color: acc.secondary,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.06em",
        padding: compact ? "2px 6px" : "4px 10px",
        textTransform: "uppercase",
      }}
      aria-label={iconOnly ? label : undefined}
    >
      <span aria-hidden={iconOnly ? undefined : true}>✦</span>
      {!iconOnly && label}
    </span>
  );
}

function SidebarCreditsCard({ acc, creditsBalance, activeSubscription, t }) {
  const cardStyle = activeSubscription
    ? {
        margin: "0 12px",
        padding: "14px 14px 13px",
        borderRadius: 14,
        background: `linear-gradient(155deg, ${hexToRgba(acc.secondary, 0.14)} 0%, ${hexToRgba(acc.primary, 0.08)} 48%, rgba(8, 12, 32, 0.4) 100%)`,
        border: `1px solid ${hexToRgba(acc.secondary, 0.38)}`,
        boxShadow: `0 10px 28px rgba(0, 0, 0, 0.35), 0 0 24px ${hexToRgba(acc.secondary, 0.12)}, inset 0 1px 0 ${hexToRgba(acc.secondary, 0.15)}`,
      }
    : {
        margin: "0 12px",
        padding: "14px 16px",
        borderRadius: 12,
        background: `${acc.primary}15`,
        border: `1px solid ${acc.primary}33`,
      };

  return (
    <div style={cardStyle}>
      <div
        style={{
          fontSize: 10,
          color: activeSubscription ? acc.secondary : acc.primary,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          marginBottom: 10,
          fontWeight: 500,
        }}
      >
        {t("credit_balance_label")}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CreditOrb acc={acc} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 28,
                fontWeight: 500,
                color: C.textPrimary,
                lineHeight: 1,
              }}
            >
              {creditsBalance}
            </span>
            <span style={{ fontSize: 12, color: C.textMuted }}>{t("credits")}</span>
          </div>
        </div>
      </div>

      {activeSubscription && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${hexToRgba(acc.secondary, 0.22)}` }}>
          <PremiumBadge acc={acc} label={t("premium_badge")} />
        </div>
      )}
    </div>
  );
}

export default function CreditsChip({ acc, creditsBalance, activeSubscription = false, compact = false, onClick }) {
  const { t } = useI18n();

  if (!compact) {
    return <SidebarCreditsCard acc={acc} creditsBalance={creditsBalance} activeSubscription={activeSubscription} t={t} />;
  }

  const balanceRow = (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <CreditOrb acc={acc} size={20} />
      <span
        style={{
          fontFamily: "'Cormorant Garamond',serif",
          fontSize: 18,
          fontWeight: 500,
          color: C.textPrimary,
        }}
      >
        {creditsBalance}
      </span>
      {activeSubscription && (
        <PremiumBadge acc={acc} label={t("premium_badge")} compact iconOnly />
      )}
    </div>
  );

  const chip = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: `${acc.primary}15`,
        border: `1px solid ${acc.primary}33`,
      }}
    >
      {balanceRow}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
        aria-label={t("credit_balance_label")}
      >
        {chip}
      </button>
    );
  }

  return chip;
}
